/**
 * @fileoverview Control Panel — the extension host at Foundry-compatible
 * `/workspace/control-panel/:scope/:rid/:sub` routes.
 *
 * THIS FILE WAS 1610 LINES AND IS NOW THE WIRING. It held ten settings pages,
 * a parallel hard-coded list of every page's name and permission, and an
 * if/else chain matching the URL slug against each one — three places that had
 * to agree, and a fourth (the sidebar) that read a differently-shaped copy of
 * the same list.
 *
 * Foundry's Control Panel is not a page with a switch in it; it is an extension
 * host, and the shipped registry was recovered from the 6.521.57 bundle: 94
 * definitions, 90 of them lazily loaded, projected into runtime state by an
 * eight-stage hydration pipeline. The packaging is recovered too —
 * `<feature>-extension/src/components/ControlPanelExtension.tsx` is a literal
 * authored path, one npm package per settings page.
 *
 *     ../palantir/mcp/foundry/evidence/ui/
 *       control-panel-default-extension-registry-6.521.57.json
 *       control-panel-exact-source-paths-6.521.57.json
 *
 * So the pages moved to `src/packages/<feature>-extension/`, the list became
 * `DEFAULT_EXTENSION_REGISTRY`, the permission checks became operations
 * resolved by `hydrateExtensionRegistry`, and the sidebar and All Settings now
 * read the same hydrated state this route resolves the URL against. Adding a
 * settings page is now one definition and one file; it was four edits, and the
 * one people forgot was the sidebar, which is why pages existed that nothing
 * linked to.
 *
 * Header ownership is unchanged: the module-owned `<ModuleHeader>` is the one
 * persistent top toolbar (Workshop UL — apps/app/docs/COMPONENT-MODEL.md), and
 * `<SettingsAppShell>` is the module body, mounted directly under `<Module>`
 * and not inside a padded `<Page>` that would double-constrain its centering.
 */

import { useMemo } from "react";

import { Module, ModuleHeader } from "../components/module";
import {
  SettingsAppMenu,
  SettingsAppMenuCategory,
  SettingsAppMenuItem,
  SettingsAppShell,
} from "../components/multipass";
import { AllSettings } from "../packages/control-panel-shell/src/components/AllSettings";
import { ExtensionHost } from "../packages/control-panel-shell/src/components/ExtensionHost";
import { LoadingBarProvider } from "../packages/control-panel-shell/src/components/LoadingBar";
import {
  EXTENSION_DISPLAY_GROUPS,
  EXTENSION_DISPLAY_GROUP_LABELS,
} from "../packages/control-panel-shell/src/registry/ExtensionDefinition";
import { useHydratedRegistry } from "../packages/control-panel-shell/src/registry/useHydratedRegistry";
import { useAuth } from "../contexts/auth-context";
import { container } from "../hex/container";
import { controlPanelExtensionPath, controlPanelPath } from "../lib/control-panel-routes";

interface SettingsProps {
  /** The sub-page slug from the URL; `all-settings` is the landing page. */
  sub?: string;
}

export function Settings({ sub = "all-settings" }: SettingsProps) {
  /*
    LINKS FOLLOW THE URL YOU ARRIVED ON. Every menu item used to be hard-coded
    to `/settings/...`, so entering through the Control Panel's own front door
    and clicking anything silently dropped you back onto /settings — the app had
    a front door and no hallway.
  */
  const config = container().config;
  const allSettingsPath = controlPanelPath(config, "enrollment", "all-settings");
  const { session } = useAuth();
  const { extensions } = useHydratedRegistry(session?.role);

  const showingAllSettings = sub === "all-settings";
  const active = useMemo(
    () =>
      extensions.find(
        (e) =>
          e.definition.urlPath === `/${sub}` ||
          e.definition.presentation.redirectUrlPaths.includes(`/${sub}`),
      ),
    [extensions, sub],
  );

  /*
    The menu is the hydrated registry grouped by display group — the same
    projection All Settings makes, so the two can no longer disagree about what
    exists. Locked pages are listed: a rail that hides them and an index that
    shows them greyed would be telling one reader two different stories.
  */
  const menu = (
    <SettingsAppMenu>
      {/* First, and outside the groups — it is the index of everything below,
          the way Foundry's Control Panel opens on All settings. */}
      <SettingsAppMenuCategory title="Overview">
        <SettingsAppMenuItem
          href={allSettingsPath}
          label="All settings"
          active={showingAllSettings}
        />
      </SettingsAppMenuCategory>
      {EXTENSION_DISPLAY_GROUPS.map((group) => {
        const items = extensions.filter(
          (e) => e.isDiscoverable && e.definition.displayGroup === group,
        );
        if (items.length === 0) return null;
        return (
          <SettingsAppMenuCategory key={group} title={EXTENSION_DISPLAY_GROUP_LABELS[group]}>
            {items.map(({ definition }) => (
              <SettingsAppMenuItem
                key={definition.id}
                href={controlPanelExtensionPath(config, definition.type, definition.urlPath)}
                label={definition.displayName}
                active={definition.id === active?.definition.id}
              />
            ))}
          </SettingsAppMenuCategory>
        );
      })}
    </SettingsAppMenu>
  );

  return (
    <Module data-testid="settings" header={<ModuleHeader title="Control Panel" />}>
      <LoadingBarProvider>
      <SettingsAppShell menu={menu}>
        {showingAllSettings ? (
          <AllSettings
            extensions={extensions}
            hrefForExtension={(definition) =>
              controlPanelExtensionPath(config, definition.type, definition.urlPath)
            }
          />
        ) : (
          <ExtensionHost extension={active} slug={sub} />
        )}
      </SettingsAppShell>
      </LoadingBarProvider>
    </Module>
  );
}
